// src/myWorkspace/WorkspacePage.js
// Home page for signed-in users: start a workspace at the top, browse existing ones below. Until
// someone has a video of their own, a getting-started panel recommends the fastest ways in.

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import './workspacePage.css';
import NewWorkspaceUploader from './workspaceComponents/NewWorkspaceUploader';
import ListViewWorkspace from './listviewWorkspace';
import GetStarted from './onboarding/GetStarted';
import useOnboarding from './onboarding/useOnboarding';
import usePlan, { isProPlan } from '../myPlans/usePlan';
import AppTransition from '../components/appComponents/AppTransition';
import { track } from '../analytics/track';

const WorkspacePage = () => {
  // True while the uploader is creating a workspace; the list holds its rows until we redirect
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const uploaderRef = useRef(null);
  const [counts, setCounts] = useState(null); // { total, own } once the list has loaded
  const [onboarding, updateOnboarding] = useOnboarding();
  const plan = usePlan();
  const isPro = isProPlan(plan);
  const canPreview = !!plan && !isPro && (plan.previewUploadsUsed ?? 0) < 1;

  const showGetStarted = !!counts && counts.own === 0 && !!onboarding && !onboarding.dismissed;

  const shownTracked = useRef(false);
  useEffect(() => {
    if (showGetStarted && !shownTracked.current) {
      shownTracked.current = true;
      track('onboarding_shown', { useCase: onboarding.useCase || 'none' });
    }
  }, [showGetStarted, onboarding]);

  const pick = id => {
    updateOnboarding({ useCase: id });
    track('onboarding_choice', { useCase: id });
  };

  const uploadNote = canPreview
    ? 'Your first upload is free to preview: its first 30 minutes'
    : isPro ? 'MP4, MOV or WebM up to 16 GB' : 'Uploading your own videos is part of Pro';

  return (
    <div className="wsp-page">
      <AppTransition>
        <div className="wsp-column">
          <h1 className="wsp-hero-title">Workspace</h1>
          <NewWorkspaceUploader ref={uploaderRef} onBusyChange={setIsCreatingWorkspace} />
          {showGetStarted && (
            <GetStarted
              useCase={onboarding.useCase}
              onPick={pick}
              onDismiss={() => { updateOnboarding({ dismissed: true }); track('onboarding_dismissed'); }}
              onUpload={() => { track('onboarding_action', { action: 'upload' }); uploaderRef.current?.openUpload(); }}
              onPasteLink={() => { track('onboarding_action', { action: 'link' }); uploaderRef.current?.focusLink(); }}
              uploadNote={uploadNote}
            />
          )}
          {counts && counts.total >= 2 && (
            <Link to="/ask" className="wsp-ask" onClick={() => track('library_entry', { from: 'workspace' })}>
              <FontAwesomeIcon icon={faMagnifyingGlass} className="wsp-ask-icon" />
              <span><b>Ask across all your videos.</b> Find which one covers something, or pull answers from several at once.</span>
              <FontAwesomeIcon icon={faArrowRight} className="wsp-ask-arrow" />
            </Link>
          )}
          <ListViewWorkspace holdUpdates={isCreatingWorkspace} onCounts={setCounts} hideEmpty={showGetStarted} />
        </div>
      </AppTransition>
    </div>
  );
};

export default WorkspacePage;
